import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChefHat, Globe, ShoppingBag, QrCode, Check, ArrowLeft, Mail, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import api, { formatApiError } from "@/lib/api";
import { InfoRequestDialog } from "@/components/InfoRequestDialog";

const CONTACT_EMAIL = "creamenuone@gmail.com";

const services = [
  {
    key: "service_menu",
    icon: ChefHat,
    name: "Compilazione menù dai nostri chef",
    price: "100",
    desc: "Ci pensiamo noi: i nostri chef compilano il menù digitale completo per la tua attività, pronto all'uso.",
    features: ["Menù completo pronto all'uso", "Categorie e piatti inseriti da noi", "Descrizioni professionali", "Foto ottimizzate"],
  },
  {
    key: "service_website",
    icon: Globe,
    name: "Creazione sito web per la tua attività",
    price: "300",
    desc: "Sviluppiamo la pagina web della tua attività, moderna e su misura.",
    features: ["Sito web professionale su misura", "Design responsive mobile e desktop", "Integrazione con il tuo menù MenuOne"],
    note: "Dominio e hosting sono a spese di chi acquista: MenuOne si occupa solo di sviluppare la pagina web.",
  },
  {
    key: "service_ecommerce",
    icon: ShoppingBag,
    name: "E-commerce per la tua attività",
    price: "600",
    desc: "Realizziamo il tuo negozio online completo per vendere i tuoi prodotti.",
    features: ["Negozio online completo", "Catalogo prodotti e carrello", "Pagamenti online integrati"],
    note: "Come per il sito web, dominio e hosting non sono inclusi e restano a carico di chi acquista.",
  },
];

export default function Services() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loadingKey, setLoadingKey] = useState(null);
  const [paidService, setPaidService] = useState(null);

  useEffect(() => {
    if (searchParams.get("paid") === "1") {
      toast.success("Pagamento riuscito! Ti contatteremo a breve.");
      const svc = services.find((s) => s.key === searchParams.get("service"));
      if (svc) setPaidService({ key: svc.key, name: svc.name });
      searchParams.delete("paid"); searchParams.delete("service"); setSearchParams(searchParams, { replace: true });
    } else if (searchParams.get("pay_cancel") === "1") {
      toast.info("Pagamento annullato.");
      searchParams.delete("pay_cancel"); setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const buy = async (lookup_key) => {
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/services/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoadingKey(null);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {paidService && <InfoRequestDialog service={paidService} open onOpenChange={(o) => { if (!o) setPaidService(null); }} showTrigger={false} />}
      <header className="h-20 border-b border-gray-100 flex items-center justify-between px-6 max-w-7xl mx-auto">
        <Link to="/"><Logo /></Link>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900" data-testid="back-home"><ArrowLeft className="h-4 w-4" />Torna al sito</Link>
      </header>

      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0 mo-glow-bg" />
        <div className="relative max-w-3xl mx-auto px-6 text-center mo-fade-up">
          <p className="text-[#5aa469] text-xs tracking-[0.25em] uppercase mb-4">Servizi</p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mb-5">Facciamo crescere la tua attività</h1>
          <p className="text-gray-600 text-lg">Servizi professionali a pagamento per portare la tua attività online, dal menù al sito fino all'e-commerce.</p>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <motion.div key={s.key} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="rounded-3xl mo-card p-8 flex flex-col" data-testid={`service-${s.key}`}>
              <div className="h-12 w-12 rounded-xl bg-[#5aa469]/12 flex items-center justify-center mb-5"><s.icon className="h-6 w-6 text-[#5aa469]" /></div>
              <h3 className="font-display font-semibold text-2xl leading-tight">{s.name}</h3>
              <div className="mt-3 mb-4 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold font-display">{s.price} €</span>
                <span className="text-gray-500 text-sm">una tantum</span>
              </div>
              <p className="text-gray-600 text-sm mb-5">{s.desc}</p>
              <ul className="space-y-2.5 mb-5 flex-1">
                {s.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-gray-700"><Check className="h-4 w-4 text-[#5aa469] mt-0.5 shrink-0" />{f}</li>)}
              </ul>
              {s.note && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3 mb-5">{s.note}</p>}
              <Button onClick={() => buy(s.key)} disabled={loadingKey === s.key} className="rounded-full w-full h-11 bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold active:scale-95 transition-transform" data-testid={`buy-${s.key}`}>
                {loadingKey === s.key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Acquista"}
              </Button>
            </motion.div>
          ))}
        </div>

        {/* Gadget QR — su richiesta */}
        <div className="max-w-6xl mx-auto px-6 mt-6">
          <div className="rounded-3xl mo-card p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6" data-testid="service-gadget">
            <div className="h-14 w-14 rounded-xl bg-[#5aa469]/12 flex items-center justify-center shrink-0"><QrCode className="h-7 w-7 text-[#5aa469]" /></div>
            <div className="flex-1">
              <h3 className="font-display font-semibold text-2xl">Gadget personalizzati con QR Code</h3>
              <p className="text-gray-600 mt-1">Ideazione, creazione e spedizione di gadget per l'applicazione dei QR Code (espositori da tavolo, vetrofanie, adesivi, targhe e altro). Il prezzo varia in base ai gadget richiesti.</p>
              <p className="text-sm text-gray-500 mt-2">Richiedi informazioni e un preventivo scrivendo a <span className="font-medium text-[#5aa469]">{CONTACT_EMAIL}</span>.</p>
            </div>
            <InfoRequestDialog />
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-8">Pagamenti sicuri gestiti da Stripe. Prezzi IVA inclusa. Per qualsiasi informazione: <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#5aa469] hover:underline">{CONTACT_EMAIL}</a></p>
      </section>

      <footer className="border-t border-gray-100 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo />
          <p className="text-sm text-gray-500">© {new Date().getFullYear()} MenuOne. Tutti i diritti riservati.</p>
        </div>
      </footer>
    </div>
  );
}
